/**
 * LineChangesModal — read-only diff view for a document's line-item changes.
 *
 * Translations are resolved via UIProvider under the key prefix
 * `components.lineChangesModal`.
 *
 * @example
 * ```tsx
 * <LineChangesModal
 *   lineChanges={entry.lineChanges as LineChangesData}
 *   changedAt={entry.changedAt}
 *   open={showModal}
 *   onClose={() => setShowModal(false)}
 * />
 * ```
 */

import { Modal, SimpleTable } from '../../components';
import { useTranslation } from '../../providers/UIProvider';
import { Temporal, plainDateFrom, formatDotDate, instantFrom } from '@insio/dates';
import { useTimeZone } from '../../providers/UIProvider';
import { useFormatDotTimestamp } from '../../hooks/dates-hooks';

// ─── Exported types ───────────────────────────────────────────────────────────

export interface FieldChange {
  from: string | null;
  to: string | null;
}

export interface LineSnapshot {
  lineNumber: number | null;
  description?: string | null;
  unit?: string | null;
  quantity?: number | null;
  unitPriceInclVat?: number | null;
  unitPriceNoVat?: number | null;
  totalAmountInclVat?: number | null;
  totalAmountNoVat?: number | null;
  vatRate?: number | null;
  vatAmount?: number | null;
  vatMode?: string | null;
  ean?: string | null;
  classification?: string | null;
  costCenterId?: string | null;
  // Requisition lines: estimates rather than agreed prices.
  estimatedUnitPrice?: number | null;
  estimatedTotalAmountNoVat?: number | null;
  /** Only in history written before the line date was removed (TAP-1210). */
  neededBy?: string | null;
  // Requisition, purchase order and invoice lines: "CODE — name" labels taken at write time.
  costCenter?: string | null;
  job?: string | null;
  // Purchase order lines.
  expectedDeliveryDate?: string | null;
  // Goods receipt lines.
  quantityReceived?: number | null;
}

/** One column of the added/removed tables; `key` is also its `fieldLabels.<key>` label. */
export interface LineSnapshotColumn {
  key: Exclude<keyof LineSnapshot, 'lineNumber'>;
  align?: 'left' | 'right';
  /** Rendered with the emphasis the total column carries. */
  strong?: boolean;
  /** Appended to a non-empty value, e.g. '%'. */
  suffix?: string;
}

/** Added/removed columns of an invoice line — the default when a caller names none. */
export const INVOICE_LINE_COLUMNS: readonly LineSnapshotColumn[] = [
  { key: 'description', align: 'left', strong: true },
  { key: 'quantity', align: 'right' },
  { key: 'unitPriceNoVat', align: 'right' },
  { key: 'vatRate', align: 'right', suffix: '%' },
  { key: 'totalAmountNoVat', align: 'right', strong: true }
];

export interface LineModification {
  lineNumber: number | null;
  fields: Record<string, FieldChange>;
}

export interface LineChangesData {
  added: LineSnapshot[];
  removed: LineSnapshot[];
  modified: LineModification[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Detects ISO date / datetime strings and formats them as dd.MM.yyyy.
 * Passes through everything else unchanged.
 */
export function formatChangeValue(
  value: string | null,
  timeZone: string = 'Europe/Prague'
): string {
  if (value == null) return '';

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    try {
      return formatDotDate(plainDateFrom(value));
    } catch {
      return value;
    }
  }

  if (/^\d{4}-\d{2}-\d{2}T[\d:.Z+-]+$/.test(value)) {
    try {
      const instant = instantFrom(value);
      const pd = instant.toZonedDateTimeISO(timeZone).toPlainDate();
      return formatDotDate(pd);
    } catch {
      return value;
    }
  }

  return value;
}

/** Hook: returns a bound formatChangeValue using the current FormatContext time zone. */
export function useFormatChangeValue(): (value: string | null) => string {
  const timeZone = useTimeZone();
  return (value: string | null) => formatChangeValue(value, timeZone);
}

// ─── Constants ────────────────────────────────────────────────────────────────

// Must mirror the backend's tracked line fields (procurement `*-history.fields.ts` and
// invoice-history/history-tracked-fields.ts).
const USER_EDITABLE_FIELDS = new Set([
  'description',
  'productType',
  'quantity',
  'unitPriceInclVat',
  'unitPriceNoVat',
  'totalAmountInclVat',
  'totalAmountNoVat',
  'vatRate',
  'vatAmount',
  'vatMode',
  'unit',
  'costCenterId',
  'discountPercent',
  'ean',
  'code',
  'recyclingFee',
  // Requisition lines.
  'estimatedUnitPrice',
  'estimatedTotalAmountNoVat',
  // Older history only.
  'neededBy',
  // Requisition, purchase order and invoice lines.
  'costCenter',
  'job',
  // Purchase order lines.
  'expectedDeliveryDate',
  // Goods receipt lines.
  'quantityReceived',
  'notes'
]);

// Fields where "00123" and "123" are the same value — safe to normalise via Number().
const NUMERIC_FIELDS = new Set([
  'quantity',
  'unitPriceInclVat',
  'unitPriceNoVat',
  'totalAmountInclVat',
  'totalAmountNoVat',
  'vatRate',
  'vatAmount',
  'discountPercent',
  'recyclingFee',
  'estimatedUnitPrice',
  'estimatedTotalAmountNoVat',
  'quantityReceived'
]);

function normalise(v: string | null): string | null {
  if (v == null) return null;
  const n = Number(v);
  return !Number.isNaN(n) && isFinite(n) ? String(n) : v;
}

function isMeaningfulChange(field: string, change: FieldChange): boolean {
  if (!USER_EDITABLE_FIELDS.has(field)) return false;
  if (NUMERIC_FIELDS.has(field)) {
    return normalise(change.from) !== normalise(change.to);
  }
  return change.from !== change.to;
}

/** The modified lines the modal shows, each stripped of the fields it would not render. */
function visibleModifications(lineChanges: LineChangesData | null | undefined): LineModification[] {
  return (lineChanges?.modified ?? [])
    .map(mod => ({
      ...mod,
      fields: Object.fromEntries(
        Object.entries(mod.fields).filter(([field, change]) => isMeaningfulChange(field, change))
      )
    }))
    .filter(mod => Object.keys(mod.fields).length > 0);
}

/** Whether the modal would show anything for this diff; an edit of a hidden field alone does not count. */
export function hasVisibleLineChanges(lineChanges: LineChangesData | null | undefined): boolean {
  if (!lineChanges) return false;
  if ((lineChanges.added?.length ?? 0) > 0 || (lineChanges.removed?.length ?? 0) > 0) return true;
  return visibleModifications(lineChanges).length > 0;
}

/** Display text of one field's raw value; enum members become labels here, dates are formatted after. */
export type LineValueFormatter = (field: string, value: string) => string;

const verbatim: LineValueFormatter = (_field, value) => value;

// ─── Props ────────────────────────────────────────────────────────────────────

export interface LineChangesModalProps {
  /** Line-change data from the document's history entry */
  lineChanges: LineChangesData | null;
  /** Columns of the added/removed tables; invoice lines when omitted. */
  columns?: readonly LineSnapshotColumn[];
  /** Turns a raw value into its label, e.g. an enum member; values pass through verbatim when omitted. */
  formatValue?: LineValueFormatter;
  /** Timestamp of the history entry — shown in the modal description */
  changedAt: Temporal.Instant | null | undefined;
  /** Whether the modal is visible */
  open: boolean;
  /** Called when the modal is dismissed */
  onClose: () => void;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionLabel({ dot, label, count }: { dot: string; label: string; count: number }) {
  return (
    <div className='flex items-center gap-sm mb-sm'>
      <span className={`w-2 h-2 rounded-full shrink-0 ${dot}`} />
      <p className='text-xs font-semibold text-foreground-base'>
        {label}
        <span className='ml-xs text-foreground-muted font-normal'>({count})</span>
      </p>
    </div>
  );
}

interface SnapshotTableProps {
  lines: LineSnapshot[];
  columns: readonly LineSnapshotColumn[];
  strikethrough?: boolean;
  t: (key: string, opts?: Record<string, unknown>) => string;
  fmtChange: (v: string | null) => string;
  formatValue: LineValueFormatter;
}

function SnapshotTable({
  lines,
  columns,
  strikethrough,
  t,
  fmtChange,
  formatValue
}: SnapshotTableProps) {
  const muted = strikethrough ? 'text-foreground-muted line-through' : '';

  const cell = (line: LineSnapshot, column: LineSnapshotColumn) => {
    const value = line[column.key];
    if (value == null || value === '') return '—';
    const text = fmtChange(formatValue(column.key, String(value)));
    return column.suffix ? `${text}${column.suffix}` : text;
  };

  return (
    <SimpleTable
      scrollable
      headers={[
        <span className='text-left w-10'>#</span>,
        ...columns.map(column => (
          <span className={column.align === 'right' ? 'text-right block' : 'text-left'}>
            {t(`fieldLabels.${column.key}`)}
          </span>
        ))
      ]}
      data={lines}
      renderRow={line => [
        <span className={`text-xs text-foreground-muted whitespace-nowrap ${muted}`}>
          {line.lineNumber ?? '—'}
        </span>,
        ...columns.map(column => {
          if (column.align !== 'right') {
            return (
              <span className={`${column.strong ? 'font-semibold' : ''} text-sm ${muted}`}>
                {cell(line, column)}
              </span>
            );
          }
          return (
            <span
              className={`block text-right tabular-nums whitespace-nowrap text-sm ${
                column.strong ? 'font-semibold' : ''
              } ${muted}`}
            >
              {cell(line, column)}
            </span>
          );
        })
      ]}
    />
  );
}

interface ModifiedTableProps {
  mods: LineModification[];
  t: (key: string, opts?: Record<string, unknown>) => string;
  fmtChange: (v: string | null) => string;
  formatValue: LineValueFormatter;
}

function ModifiedTable({ mods, t, fmtChange, formatValue }: ModifiedTableProps) {
  type FlatRow = {
    lineNumber: number | null;
    field: string;
    fieldLabel: string;
    change: FieldChange;
    isGroupStart: boolean;
  };

  const rows: FlatRow[] = mods.flatMap((mod, mi) =>
    Object.entries(mod.fields).map(([field, change], fi) => {
      const labelKey = `fieldLabels.${field}`;
      const translated = t(labelKey);
      return {
        lineNumber: mod.lineNumber,
        field,
        fieldLabel: translated !== labelKey ? translated : field,
        change,
        isGroupStart: fi === 0 && mi > 0
      };
    })
  );

  return (
    <SimpleTable
      scrollable
      headers={[
        <span className='text-left w-10'>#</span>,
        <span className='text-left'>{t('modal.field')}</span>,
        <span className='text-right block'>{t('modal.from')}</span>,
        <span className='text-right block'>{t('modal.to')}</span>
      ]}
      data={rows}
      getRowClassName={row => (row.isGroupStart ? 'border-t-2 border-border-strong' : '')}
      renderRow={(row, i) => {
        const isFirstField =
          i === 0 || rows[i - 1]?.lineNumber !== row.lineNumber || row.isGroupStart;
        return [
          <span className='text-xs text-foreground-muted align-top whitespace-nowrap'>
            {isFirstField ? (row.lineNumber ?? '?') : ''}
          </span>,
          <span className='whitespace-nowrap text-foreground-muted text-sm'>{row.fieldLabel}</span>,
          <span className='block text-right line-through text-foreground-muted tabular-nums whitespace-nowrap text-sm'>
            {row.change.from != null ? fmtChange(formatValue(row.field, row.change.from)) : '—'}
          </span>,
          <span className='block text-right font-semibold tabular-nums whitespace-nowrap text-sm'>
            {row.change.to != null ? (
              fmtChange(formatValue(row.field, row.change.to))
            ) : (
              <span className='italic font-normal text-foreground-muted'>
                {t('lineChanges.fieldCleared')}
              </span>
            )}
          </span>
        ];
      }}
    />
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export function LineChangesModal({
  lineChanges,
  columns = INVOICE_LINE_COLUMNS,
  formatValue = verbatim,
  changedAt,
  open,
  onClose
}: LineChangesModalProps) {
  const t = useTranslation('components.lineChangesModal');
  const formatTs = useFormatDotTimestamp();
  const fmtChange = useFormatChangeValue();

  const meaningfulModified = visibleModifications(lineChanges);

  const addedCount = lineChanges?.added?.length ?? 0;
  const removedCount = lineChanges?.removed?.length ?? 0;
  const modifiedCount = meaningfulModified.length;
  const isEmpty = addedCount === 0 && removedCount === 0 && modifiedCount === 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size='2xl'
      className='w-[calc(100vw-2rem)]! md:w-250! max-h-[90vh] flex flex-col overflow-hidden'
    >
      <Modal.Header
        onClose={onClose}
        label={t('modal.lineChanges')}
        description={changedAt ? formatTs(changedAt) : undefined}
      />
      <Modal.Content className='p-md! flex-1 overflow-y-auto min-h-0'>
        <div className='w-full space-y-lg'>
          {addedCount > 0 && (
            <div>
              <SectionLabel
                dot='bg-success-text'
                label={t('modal.addedLines')}
                count={addedCount}
              />
              <SnapshotTable
                lines={lineChanges!.added}
                columns={columns}
                t={t}
                fmtChange={fmtChange}
                formatValue={formatValue}
              />
            </div>
          )}

          {removedCount > 0 && (
            <div>
              <SectionLabel
                dot='bg-error-text'
                label={t('modal.removedLines')}
                count={removedCount}
              />
              <SnapshotTable
                lines={lineChanges!.removed}
                columns={columns}
                strikethrough
                t={t}
                fmtChange={fmtChange}
                formatValue={formatValue}
              />
            </div>
          )}

          {modifiedCount > 0 && (
            <div>
              <SectionLabel
                dot='bg-warn-text'
                label={t('modal.modifiedLines')}
                count={modifiedCount}
              />
              <ModifiedTable
                mods={meaningfulModified}
                t={t}
                fmtChange={fmtChange}
                formatValue={formatValue}
              />
            </div>
          )}

          {isEmpty && (
            <p className='text-sm text-foreground-muted text-center py-lg'>
              {t('modal.noDetails')}
            </p>
          )}
        </div>
      </Modal.Content>
    </Modal>
  );
}

LineChangesModal.displayName = 'LineChangesModal';
